import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faTrash } from "@fortawesome/free-solid-svg-icons";
import "./styles/RunMatrixModal.css";

const SECTION = { marginBottom: 20 };
const SECTION_TITLE = { fontSize: 11, fontWeight: 700, color: "#60a5fa", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 };
const DIVIDER = { borderTop: "1px solid #1e2d45", margin: "16px 0" };
const INPUT_STYLE = {
  width: "100%", boxSizing: "border-box",
  background: "#0f172a", border: "1px solid #334155",
  color: "#e2e8f0", borderRadius: 5,
  padding: "5px 8px", fontSize: 12, outline: "none",
};
const SELECT_STYLE = { ...INPUT_STYLE, padding: "5px 4px" };

let _rowSeq = 0;
function newRowId() {
  _rowSeq += 1;
  return `row_${Date.now()}_${_rowSeq}`;
}

function emptyRow(defaultBannerName) {
  return {
    id: newRowId(),
    label: "",
    bannerName: defaultBannerName || "",
    weight: "",
    filter: "ALL",
    outputSetNames: [],
    enabled: true,
    sigTestMode: "default", // "default" (use global Sig Testing setting) | "on" | "off"
    sigTestWeighted: true,
  };
}

// Rows saved before this field existed have no `enabled` key — treat that as
// included, so old saved matrices keep running exactly as before.
const isRowEnabled = (row) => row.enabled !== false;

// ── Cross-product generator ───────────────────────────────────────────────
function CrossProductGenerator({ banners, weightNames, onGenerate }) {
  const [open, setOpen] = useState(false);
  const [selBanners, setSelBanners] = useState([]);
  const [selWeights, setSelWeights] = useState([""]); // "" = unweighted
  const [filterText, setFilterText] = useState("ALL");

  const toggle = (list, setList, value) => {
    setList(list.includes(value) ? list.filter(v => v !== value) : [...list, value]);
  };

  const generate = () => {
    const bannerNames = selBanners.length ? selBanners : [""];
    const weights = selWeights.length ? selWeights : [""];
    const filters = filterText.split(";").map(f => f.trim()).filter(Boolean);
    const filterList = filters.length ? filters : ["ALL"];

    const rows = [];
    bannerNames.forEach(bn => {
      weights.forEach(w => {
        filterList.forEach(f => {
          const row = emptyRow(bn);
          row.weight = w;
          row.filter = f;
          row.label = [bn || "Topline", w || "Unweighted", f !== "ALL" ? f : null]
            .filter(Boolean).join(" · ");
          rows.push(row);
        });
      });
    });
    onGenerate(rows);
    setOpen(false);
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: "#1e3a5f", border: "1px dashed #334155", color: "#60a5fa",
          borderRadius: 7, padding: "6px 12px", fontSize: 12, cursor: "pointer",
        }}
      >
        {open ? "▲" : "▼"} Generate rows from banners × weights × filters
      </button>
      {open && (
        <div style={{ background: "#0c1928", border: "1px solid #1e3a5f", borderRadius: 8, padding: 12, marginTop: 8 }}>
          <div style={{ fontSize: 10, color: "#8a9bb0", marginBottom: 4 }}>Banners</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
            {(banners || []).map(b => (
              <label key={b.name} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#cbd5e1" }}>
                <input type="checkbox" checked={selBanners.includes(b.name)}
                  onChange={() => toggle(selBanners, setSelBanners, b.name)} />
                {b.name}
              </label>
            ))}
            {(!banners || banners.length === 0) && <span style={{ fontSize: 11, color: "#475569" }}>No banners available</span>}
          </div>

          <div style={{ fontSize: 10, color: "#8a9bb0", marginBottom: 4 }}>Weights</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#cbd5e1" }}>
              <input type="checkbox" checked={selWeights.includes("")}
                onChange={() => toggle(selWeights, setSelWeights, "")} />
              Unweighted
            </label>
            {(weightNames || []).map(w => (
              <label key={w} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#cbd5e1" }}>
                <input type="checkbox" checked={selWeights.includes(w)}
                  onChange={() => toggle(selWeights, setSelWeights, w)} />
                {w}
              </label>
            ))}
          </div>

          <div style={{ fontSize: 10, color: "#8a9bb0", marginBottom: 4 }}>Filters (; separated, blank = ALL)</div>
          <input
            type="text" value={filterText} onChange={e => setFilterText(e.target.value)}
            placeholder="ALL;S1=1"
            style={{ ...INPUT_STYLE, marginBottom: 10 }}
          />

          <button
            onClick={generate}
            style={{
              background: "#2563eb", border: "none", color: "#fff",
              borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontWeight: 600,
            }}
          >
            + Add {Math.max(1, selBanners.length || 1) * Math.max(1, selWeights.length || 1) *
              Math.max(1, filterText.split(";").map(f => f.trim()).filter(Boolean).length || 1)} row(s)
          </button>
        </div>
      )}
    </div>
  );
}

export default function RunMatrixModal({
  open, onClose,
  banners, savedWeightings, outputSets,
  runs, onChangeRuns,
  onRunAll, onDownloadAll,
  batchStatus, batchProgress, batchRows,
  bilendiEnabled,
}) {
  if (!open) return null;

  const weightNames = Object.keys(savedWeightings || {});
  const isBusy = batchStatus === "running";
  const enabledCount = (runs || []).filter(isRowEnabled).length;

  const addRow = () => onChangeRuns([...(runs || []), emptyRow(banners && banners[0] ? banners[0].name : "")]);
  const removeRow = (id) => onChangeRuns((runs || []).filter(r => r.id !== id));
  const updateRow = (id, field, value) =>
    onChangeRuns((runs || []).map(r => (r.id === id ? { ...r, [field]: value } : r)));
  const toggleOutputSet = (id, setName) => {
    onChangeRuns((runs || []).map(r => {
      if (r.id !== id) return r;
      const has = (r.outputSetNames || []).includes(setName);
      return {
        ...r,
        outputSetNames: has
          ? r.outputSetNames.filter(n => n !== setName)
          : [...(r.outputSetNames || []), setName],
      };
    }));
  };
  const duplicateRow = (id) => {
    const src = (runs || []).find(r => r.id === id);
    if (!src) return;
    onChangeRuns([...(runs || []), { ...src, id: newRowId(), label: src.label ? `${src.label} (copy)` : "" }]);
  };
  const clearAllRows = () => {
    if (!(runs || []).length) return;
    if (window.confirm(`Remove all ${runs.length} row(s) from the Run Matrix? This cannot be undone.`)) {
      onChangeRuns([]);
    }
  };

  const rowStatusFor = (idx) => (batchRows && batchRows[idx]) || null;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.65)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
      onClick={e => e.target === e.currentTarget && !isBusy && onClose()}
    >
      <div
        className="run-matrix-modal"
        style={{
          background: "#0f172a", border: "1px solid #1e2d45",
          borderRadius: 12, width: 1000, maxWidth: "96vw",
          maxHeight: "90vh", overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
          padding: "24px 28px",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#e2e8f0" }}>Run Matrix</div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
              Run and export many banner × weight × filter × output-set combinations together.
            </div>
          </div>
          <button
            onClick={() => !isBusy && onClose()}
            disabled={isBusy}
            style={{ background: "none", border: "none", color: "#64748b", fontSize: 18, cursor: isBusy ? "not-allowed" : "pointer", padding: "2px 6px" }}
          >×</button>
        </div>

        <div style={DIVIDER} />

        <div style={SECTION}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={SECTION_TITLE}>Rows</div>
            <button
              onClick={clearAllRows}
              disabled={isBusy || !(runs || []).length}
              title="Remove every row from this Run Matrix"
              style={{
                background: "none", border: "1px solid #4c2a2a", color: "#f87171",
                borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 600,
                cursor: (isBusy || !(runs || []).length) ? "not-allowed" : "pointer",
                opacity: (isBusy || !(runs || []).length) ? 0.45 : 1,
              }}
            >
              Clear All
            </button>
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginBottom: 10 }}>
            Each row is computed and exported independently — the same banner can appear in
            multiple rows with a different weight, filter, or set of tables. Untick
            <strong style={{ color: "#93c5fd" }}> Run?</strong> to keep a row saved without
            including it the next time you run or download. The
            <strong style={{ color: "#93c5fd" }}> Sig Test</strong> column overrides this row's
            significance testing independently of the global Sig Testing setting.
            {!bilendiEnabled && (
              <> The <strong style={{ color: "#93c5fd" }}>Filter</strong> column only takes effect
              when Bilendi Format is enabled (toggle it in the panel behind this dialog) — it's
              currently ignored, matching how filters work outside Bilendi today.</>
            )}
          </div>

          <CrossProductGenerator
            banners={banners}
            weightNames={weightNames}
            onGenerate={(newRows) => onChangeRuns([...(runs || []), ...newRows])}
          />

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ color: "#8a9bb0", textAlign: "left" }}>
                  <th style={{ padding: "4px 6px", width: "13%" }}>Label</th>
                  <th style={{ padding: "4px 6px", width: "13%" }}>Banner</th>
                  <th style={{ padding: "4px 6px", width: "10%" }}>Weight</th>
                  <th style={{ padding: "4px 6px", width: "10%" }}>Filter</th>
                  <th style={{ padding: "4px 6px", width: "16%" }}>Output Sets</th>
                  <th style={{ padding: "4px 6px", width: "13%" }}>Sig Test</th>
                  <th style={{ padding: "4px 6px", width: "6%", textAlign: "center" }} title="Include this row the next time you Run All / Download All">Run?</th>
                  <th style={{ padding: "4px 6px", width: "9%" }}>Status</th>
                  <th style={{ padding: "4px 6px", width: "10%" }}></th>
                </tr>
              </thead>
              <tbody>
                {(runs || []).map((row, idx) => {
                  const status = rowStatusFor(idx);
                  const rowOn = isRowEnabled(row);
                  const sigMode = row.sigTestMode || "default";
                  return (
                    <tr key={row.id} style={{ borderTop: "1px solid #1e2d45", opacity: rowOn ? 1 : 0.5 }}>
                      <td style={{ padding: "4px 6px" }}>
                        <input
                          type="text" value={row.label}
                          placeholder={row.bannerName || "Topline"}
                          onChange={e => updateRow(row.id, "label", e.target.value)}
                          style={INPUT_STYLE}
                        />
                      </td>
                      <td style={{ padding: "4px 6px" }}>
                        <select
                          value={row.bannerName}
                          onChange={e => updateRow(row.id, "bannerName", e.target.value)}
                          style={SELECT_STYLE}
                        >
                          <option value="">Topline (None)</option>
                          {(banners || []).map(b => (
                            <option key={b.name} value={b.name}>{b.name}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: "4px 6px" }}>
                        <select
                          value={row.weight}
                          onChange={e => updateRow(row.id, "weight", e.target.value)}
                          style={SELECT_STYLE}
                        >
                          <option value="">Unweighted</option>
                          {weightNames.map(w => (
                            <option key={w} value={w}>{w}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: "4px 6px" }}>
                        <input
                          type="text" value={row.filter}
                          onChange={e => updateRow(row.id, "filter", e.target.value)}
                          style={{ ...INPUT_STYLE, opacity: bilendiEnabled ? 1 : 0.55 }}
                          title={bilendiEnabled ? "" : "Ignored unless Bilendi Format is enabled"}
                        />
                      </td>
                      <td style={{ padding: "4px 6px" }}>
                        {(outputSets || []).length === 0 ? (
                          <span style={{ fontSize: 10, color: "#475569" }}>All tables (no output sets defined)</span>
                        ) : (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {outputSets.map(s => (
                              <label key={s.name} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 10, color: "#cbd5e1" }}>
                                <input
                                  type="checkbox"
                                  checked={(row.outputSetNames || []).includes(s.name)}
                                  onChange={() => toggleOutputSet(row.id, s.name)}
                                />
                                {s.name}
                              </label>
                            ))}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "4px 6px" }}>
                        <select
                          value={sigMode === "on" ? (row.sigTestWeighted === false ? "on_unweighted" : "on_weighted") : sigMode}
                          onChange={e => {
                            const v = e.target.value;
                            if (v === "on_weighted") {
                              updateRow(row.id, "sigTestMode", "on");
                              updateRow(row.id, "sigTestWeighted", true);
                            } else if (v === "on_unweighted") {
                              updateRow(row.id, "sigTestMode", "on");
                              updateRow(row.id, "sigTestWeighted", false);
                            } else {
                              updateRow(row.id, "sigTestMode", v);
                            }
                          }}
                          style={SELECT_STYLE}
                          title="Overrides the global Sig Testing setting for this row only"
                        >
                          <option value="default">Use Global</option>
                          <option value="on_weighted">On (Weighted)</option>
                          <option value="on_unweighted">On (Unweighted)</option>
                          <option value="off">Off</option>
                        </select>
                      </td>
                      <td style={{ padding: "4px 6px", textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={rowOn}
                          onChange={() => updateRow(row.id, "enabled", !rowOn)}
                          title={rowOn ? "Included in Run All / Download All" : "Skipped by Run All / Download All"}
                          style={{ width: 15, height: 15, cursor: "pointer" }}
                        />
                      </td>
                      <td style={{ padding: "4px 6px", whiteSpace: "nowrap" }}>
                        {status ? (
                          <span style={{
                            fontSize: 10, fontWeight: 600, borderRadius: 4, padding: "2px 6px",
                            background: status.status === "done" ? "#c6f6d5"
                              : status.status === "error" ? "#fed7d7"
                              : status.status === "running" ? "#fef3c7"
                              : status.status === "skipped" ? "#e2e8f0" : "#e2e8f0",
                            color: status.status === "done" ? "#276749"
                              : status.status === "error" ? "#9b2c2c"
                              : status.status === "running" ? "#92400e"
                              : status.status === "skipped" ? "#64748b" : "#475569",
                          }} title={status.error || ""}>
                            {status.status}
                          </span>
                        ) : <span style={{ fontSize: 10, color: "#475569" }}>—</span>}
                      </td>
                      <td style={{ padding: "4px 6px", whiteSpace: "nowrap" }}>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={() => duplicateRow(row.id)} title="Duplicate row"
                            style={{
                              display: "flex", alignItems: "center", justifyContent: "center",
                              background: "#131f2e", border: "1px solid #334155", color: "#93c5fd",
                              borderRadius: 5, width: 26, height: 26, padding: 0,
                              cursor: "pointer", fontSize: 12, flexShrink: 0,
                            }}>
                            <FontAwesomeIcon icon={faCopy} />
                          </button>
                          <button onClick={() => removeRow(row.id)} title="Remove row"
                            style={{
                              display: "flex", alignItems: "center", justifyContent: "center",
                              background: "#2a1414", border: "1px solid #4c2a2a", color: "#f87171",
                              borderRadius: 5, width: 26, height: 26, padding: 0,
                              cursor: "pointer", fontSize: 12, flexShrink: 0,
                            }}>
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <button
            onClick={addRow}
            style={{
              width: "100%", background: "#1e3a5f", border: "1px dashed #334155",
              color: "#60a5fa", borderRadius: 7, padding: "7px 0",
              fontSize: 12, cursor: "pointer", marginTop: 10,
            }}
          >
            + Add Row
          </button>
        </div>

        <div style={DIVIDER} />

        {/* Batch progress */}
        {batchStatus && batchStatus !== "idle" && (
          <div style={{ marginBottom: 14, fontSize: 11, color: "#8a9bb0" }}>
            Batch status: <strong style={{ color: "#e2e8f0" }}>{batchStatus}</strong>
            {typeof batchProgress === "number" && batchProgress >= 0 && (
              <span> — {batchProgress}%</span>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onClose}
            disabled={isBusy}
            style={{
              background: "transparent", border: "1px solid #334155", color: "#94a3b8",
              borderRadius: 7, padding: "8px 18px", fontSize: 13, cursor: isBusy ? "not-allowed" : "pointer",
            }}
          >
            Close
          </button>
          <button
            onClick={onDownloadAll}
            disabled={isBusy || !enabledCount}
            title={!enabledCount && (runs || []).length ? "All rows are unticked in the Run? column" : ""}
            style={{
              background: "#334155", color: "#fff", border: "none", borderRadius: 7,
              padding: "8px 18px", fontSize: 13, fontWeight: 600,
              cursor: (isBusy || !enabledCount) ? "not-allowed" : "pointer",
              opacity: (isBusy || !enabledCount) ? 0.6 : 1,
            }}
          >
            Download All (zip){enabledCount ? ` (${enabledCount})` : ""}
          </button>
          <button
            onClick={onRunAll}
            disabled={isBusy || !enabledCount}
            title={!enabledCount && (runs || []).length ? "All rows are unticked in the Run? column" : ""}
            style={{
              background: "linear-gradient(135deg,#1d4ed8,#2563eb)",
              color: "#fff", border: "none", borderRadius: 7,
              padding: "8px 22px", fontSize: 13, fontWeight: 600,
              cursor: (isBusy || !enabledCount) ? "not-allowed" : "pointer",
              opacity: (isBusy || !enabledCount) ? 0.6 : 1,
            }}
          >
            {isBusy ? "Running…" : `Run All Rows${enabledCount ? ` (${enabledCount})` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
